import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { maternityService } from '../services/maternityService'
import { Pregnancy } from '../types/maternity'
import { formatDate } from '../utils/locale'
import { calculateGestationalAge } from '../utils/dueDateCalculator'
import { Modal } from './Modal'
import { PregnancyForm } from './PregnancyForm'
import './PregnancyList.css'

interface PregnancyListProps {
  patientId: string
  onPregnancyClick?: (pregnancy: Pregnancy) => void
  readOnly?: boolean
}

export function PregnancyList({ patientId, onPregnancyClick, readOnly = false }: PregnancyListProps) {
  const [showForm, setShowForm] = useState(false)
  const [editingPregnancy, setEditingPregnancy] = useState<Pregnancy | undefined>()
  const canWrite = !readOnly

  const { data: pregnancies = [], isLoading, error } = useQuery({
    queryKey: ['pregnancies', patientId],
    queryFn: () => maternityService.getPregnancies(patientId),
    enabled: !!patientId,
  })

  const handleAdd = () => {
    if (!canWrite) return
    setEditingPregnancy(undefined)
    setShowForm(true)
  }

  const handleEdit = (pregnancy: Pregnancy) => {
    if (!canWrite) return
    setEditingPregnancy(pregnancy)
    setShowForm(true)
  }

  const handleFormSuccess = () => {
    setShowForm(false)
    setEditingPregnancy(undefined)
  }

  if (isLoading) {
    return <div className="loading">Loading pregnancies...</div>
  }

  if (error) {
    return <div className="error">Error loading pregnancies</div>
  }

  return (
    <div className="pregnancy-list">
      <div className="pregnancy-list-header">
        <h2>Pregnancies</h2>
        <button
          className="btn-primary"
          onClick={handleAdd}
          disabled={!canWrite}
          title={canWrite ? undefined : 'Secondary records are read-only'}
        >
          New Pregnancy
        </button>
      </div>

      {pregnancies.length === 0 ? (
        <div className="empty-state">
          <p>No pregnancies recorded for this patient.</p>
          <button
            className="btn-primary"
            onClick={handleAdd}
            disabled={!canWrite}
            title={canWrite ? undefined : 'Secondary records are read-only'}
          >
            Record First Pregnancy
          </button>
        </div>
      ) : (
        <div className="pregnancy-cards">
          {pregnancies.map((pregnancy) => {
            const gestationalAge = calculateGestationalAge(pregnancy.lastMenstrualPeriod)
            const isActive = pregnancy.status === 'active'
            
            return (
              <div
                key={pregnancy.pregnancyId}
                className={`pregnancy-card ${isActive ? 'active' : ''}`}
                onClick={() => onPregnancyClick?.(pregnancy)}
              >
                <div className="pregnancy-card-header">
                  <div className="pregnancy-number">Pregnancy #{pregnancy.pregnancyNumber}</div>
                  <span className={`status-badge ${pregnancy.status}`}>
                    {pregnancy.status}
                  </span>
                </div>
                <div className="pregnancy-card-body">
                  <div className="pregnancy-info-row">
                    <span className="label">LMP:</span>
                    <span className="value">{formatDate(pregnancy.lastMenstrualPeriod)}</span>
                  </div>
                  <div className="pregnancy-info-row">
                    <span className="label">EDD:</span>
                    <span className="value">{formatDate(pregnancy.estimatedDueDate)}</span>
                  </div>
                  {isActive && (
                    <div className="pregnancy-info-row">
                      <span className="label">Gestational Age:</span>
                      <span className="value">{gestationalAge.display}</span>
                    </div>
                  )}
                  {pregnancy.outcome && (
                    <div className="pregnancy-info-row">
                      <span className="label">Outcome:</span>
                      <span className="value">{pregnancy.outcome}</span>
                      {pregnancy.outcomeDate && (
                        <span className="value"> ({formatDate(pregnancy.outcomeDate)})</span>
                      )}
                    </div>
                  )}
                  {pregnancy.babies && pregnancy.babies.length > 0 && (
                    <div className="pregnancy-info-row">
                      <span className="label">Babies:</span>
                      <span className="value">{pregnancy.babies.length}</span>
                    </div>
                  )}
                </div>
                <div className="pregnancy-card-actions">
                  <button
                    className="btn-link"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEdit(pregnancy)
                    }}
                    disabled={!canWrite}
                    title={canWrite ? undefined : 'Secondary records are read-only'}
                  >
                    Edit
                  </button>
                  {onPregnancyClick && (
                    <button
                      className="btn-link"
                      onClick={(e) => {
                        e.stopPropagation()
                        onPregnancyClick(pregnancy)
                      }}
                    >
                      View Details →
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <Modal
        isOpen={canWrite && showForm}
        title={editingPregnancy ? 'Edit Pregnancy' : 'New Pregnancy'}
        onClose={() => {
          setShowForm(false)
          setEditingPregnancy(undefined)
        }}
      >
        <PregnancyForm
          patientId={patientId}
          pregnancy={editingPregnancy}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setShowForm(false)
            setEditingPregnancy(undefined)
          }}
        />
      </Modal>
    </div>
  )
}
